import { useEffect, useMemo, useRef, useState } from 'react';

import { api } from '../services/api';
import {
  isColumnEligibleForSumPart,
  operatorsForColumn,
} from '../services/operatorFilters';
import {
  OPERATOR_OPTIONS,
  renderRuleDescription,
} from '../services/ruleText';
import {
  isSumOperator,
  type ColumnInference,
  type CrossFieldRule,
  type HitSeverity,
  type ReviewerDefinedRuleRequest,
  type ReviewerDefinedRuleResponse,
  type RuleOperator,
} from '../services/types';

import { Button } from './Button';

const DEFAULT_TOLERANCE = 0.01;

export interface AddRuleModalProps {
  runId: string;
  /** Run's column inference list (RAS output). Used for the col_a /
   *  col_b / parts dropdowns and operator-by-type filtering. */
  columns: ColumnInference[];
  isOpen: boolean;
  onClose: () => void;
  /** Called once the backend accepts the rule and re-kicks the run.
   *  The parent typically refreshes the run summary in response. */
  onCreated?: (response: ReviewerDefinedRuleResponse) => void;
  reviewerId: string;
}

/**
 * v0.5 Stage E.3 — modal for the reviewer to author a cross-field
 * rule from scratch.
 *
 * Layout
 * ------
 *   ┌─ header (title + close) ─────────────────────────────────┐
 *   │  col_a dropdown                                          │
 *   │  operator dropdown (filtered by col_a type)              │
 *   │  pairwise: col_b dropdown                                │
 *   │  not_null_when: condition col + value                    │
 *   │  sum_*: parts-list (chips + add) + tolerance input       │
 *   │  severity dropdown                                       │
 *   │  description textarea (auto-filled, editable)            │
 *   │  submit / cancel + inline error                          │
 *   └──────────────────────────────────────────────────────────┘
 *
 * Operator filtering: every column has a type-derived eligible
 * operator set (see ``services/operatorFilters``). Mixed-type
 * columns (v0.5 Stage C.2 ``inferred_type === "mixed"``) get the
 * intersection of eligible operators across the picked
 * ``type_union`` — conservative, prevents the reviewer from
 * authoring a rule that's nonsensical for some rows.
 *
 * Behaviour: ``onCreated`` fires once the backend accepts the rule
 * (the reviewer-defined audit lands and the pipeline re-kicks).
 * The auto-confirm flow (DECISIONS Q1) means the reviewer does NOT
 * see a confirm-rule HITL after closing this modal — the rule
 * lands on the validator directly.
 */
export function AddRuleModal({
  runId,
  columns,
  isOpen,
  onClose,
  onCreated,
  reviewerId,
}: AddRuleModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);

  const [colA, setColA] = useState<string>('');
  const [operator, setOperator] = useState<RuleOperator>('lte');
  const [colB, setColB] = useState<string>('');
  const [sumColumns, setSumColumns] = useState<string[]>([]);
  const [tolerance, setTolerance] = useState<number>(DEFAULT_TOLERANCE);
  const [conditionCol, setConditionCol] = useState<string>('');
  const [conditionValue, setConditionValue] = useState<string>('');
  const [severity, setSeverity] = useState<HitSeverity>('high');
  const [description, setDescription] = useState<string>('');
  const [descriptionTouched, setDescriptionTouched] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset on open so the modal always starts fresh.
  useEffect(() => {
    if (!isOpen) return;
    setColA(columns[0]?.column ?? '');
    setOperator('lte');
    setColB('');
    setSumColumns([]);
    setTolerance(DEFAULT_TOLERANCE);
    setConditionCol(columns[0]?.column ?? '');
    setConditionValue('');
    setSeverity('high');
    setDescription('');
    setDescriptionTouched(false);
    setPending(false);
    setError(null);
  }, [isOpen, columns]);

  const colAEntry = useMemo(
    () => columns.find((c) => c.column === colA),
    [columns, colA],
  );

  const eligibleOps = useMemo(() => {
    if (!colAEntry) return new Set<RuleOperator>();
    return operatorsForColumn({
      inferredType: colAEntry.inferred_type,
      typeUnion: colAEntry.type_union ?? null,
    });
  }, [colAEntry]);

  // When col_a changes, snap the operator to one that's eligible for
  // the new column. Otherwise the reviewer would see a Submit-blocked
  // form with no obvious cause.
  useEffect(() => {
    if (eligibleOps.size === 0) return;
    if (!eligibleOps.has(operator)) {
      // Prefer ``lte`` if eligible (most common cross-field rule
      // shape); otherwise pick the first eligible operator from the
      // dropdown's display order.
      const fallback: RuleOperator =
        eligibleOps.has('lte')
          ? 'lte'
          : (OPERATOR_OPTIONS.find((o) => eligibleOps.has(o.value))?.value ??
            'ne');
      setOperator(fallback);
    }
  }, [eligibleOps, operator]);

  const isSum = isSumOperator(operator);
  const isNotNullWhen = operator === 'not_null_when';
  const sumPartCandidates = useMemo(
    () =>
      columns.filter(
        (c) =>
          c.column !== colA &&
          isColumnEligibleForSumPart({
            inferredType: c.inferred_type,
            typeUnion: c.type_union ?? null,
          }),
      ),
    [columns, colA],
  );
  const colBCandidates = useMemo(
    () => columns.filter((c) => c.column !== colA),
    [columns, colA],
  );

  // Live preview rule for the description placeholder + summary.
  const previewRule: CrossFieldRule = useMemo(() => {
    const base: CrossFieldRule = {
      col_a: colA,
      operator,
      severity,
    };
    if (isSum) {
      base.sum_columns = sumColumns;
      base.tolerance = tolerance;
    } else if (isNotNullWhen) {
      base.col_b = conditionCol;
      if (conditionCol && conditionValue) {
        base.condition = { col: conditionCol, value: conditionValue };
      }
    } else {
      base.col_b = colB;
    }
    return base;
  }, [
    colA,
    operator,
    severity,
    isSum,
    isNotNullWhen,
    sumColumns,
    tolerance,
    colB,
    conditionCol,
    conditionValue,
  ]);

  const synthesisedDescription = useMemo(
    () => renderRuleDescription({ ...previewRule, description: undefined }),
    [previewRule],
  );

  // Validation gating for Submit. The backend re-validates, but
  // checking here gives the reviewer immediate feedback rather than
  // an after-the-fact 400.
  const validationErrors: string[] = [];
  if (!colA) validationErrors.push('Pick a column for col_a.');
  if (!eligibleOps.has(operator)) {
    validationErrors.push(
      `${operator} is not valid for ${colA || 'this column'}.`,
    );
  }
  if (isSum) {
    if (sumColumns.length === 0) {
      validationErrors.push('Add at least one part column.');
    }
  } else if (isNotNullWhen) {
    if (!conditionCol) validationErrors.push('Pick a condition column.');
    if (!conditionValue.trim())
      validationErrors.push('Enter a condition value.');
  } else {
    if (!colB) validationErrors.push('Pick a column for col_b.');
  }

  const submitDisabled = pending || validationErrors.length > 0;

  // Backdrop dismissal + Escape close. Skipped while a request is
  // in-flight to avoid losing the rule mid-write.
  useEffect(() => {
    if (!isOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !pending) onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, pending, onClose]);

  if (!isOpen) return null;

  async function onSubmit() {
    if (submitDisabled) return;
    const body: ReviewerDefinedRuleRequest = {
      col_a: colA,
      operator,
      reviewer_id: reviewerId,
      severity,
    };
    if (isSum) {
      body.sum_columns = sumColumns;
      if (tolerance !== DEFAULT_TOLERANCE) body.tolerance = tolerance;
    } else if (isNotNullWhen) {
      body.col_b = conditionCol;
      body.condition = { col: conditionCol, value: conditionValue };
    } else {
      body.col_b = colB;
    }
    if (description.trim()) {
      body.description = description.trim();
    }

    setPending(true);
    setError(null);
    try {
      const res = await api.createReviewerDefinedRule(runId, body);
      if (onCreated) onCreated(res);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create rule.');
    } finally {
      setPending(false);
    }
  }

  function addSumPart(col: string) {
    if (!col || sumColumns.includes(col)) return;
    setSumColumns([...sumColumns, col]);
  }

  function removeSumPart(col: string) {
    setSumColumns(sumColumns.filter((c) => c !== col));
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-ink/40 px-4 py-8 overflow-y-auto"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget && !pending) onClose();
      }}
      data-testid="add-rule-modal"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-rule-modal-title"
        className="w-full max-w-2xl rounded-lg bg-paper shadow-card"
      >
        <header className="flex items-start justify-between gap-4 border-b border-border px-6 py-4">
          <h2
            id="add-rule-modal-title"
            className="text-lg font-semibold"
          >
            Add cross-field rule
          </h2>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={pending}
            data-testid="add-rule-cancel"
          >
            Cancel
          </Button>
        </header>

        <div className="space-y-4 px-6 py-5">
          {/* col_a */}
          <label className="block space-y-1">
            <span className="text-xs uppercase tracking-wide text-muted">
              Column (col_a)
            </span>
            <select
              value={colA}
              onChange={(e) => setColA(e.target.value)}
              disabled={pending}
              className="block w-full rounded border border-border bg-paper px-2 py-1 text-sm font-mono"
              data-testid="add-rule-col-a"
            >
              {columns.map((c) => (
                <option key={c.column} value={c.column}>
                  {c.column}{' '}
                  ({c.inferred_type}
                  {c.inferred_type === 'mixed' && c.type_union
                    ? `: ${c.type_union.join('+')}`
                    : ''}
                  )
                </option>
              ))}
            </select>
          </label>

          {/* Operator (filtered by col_a type) */}
          <label className="block space-y-1">
            <span className="text-xs uppercase tracking-wide text-muted">
              Operator
            </span>
            <select
              value={operator}
              onChange={(e) => setOperator(e.target.value as RuleOperator)}
              disabled={pending || eligibleOps.size === 0}
              className="block w-full rounded border border-border bg-paper px-2 py-1 text-sm"
              data-testid="add-rule-operator"
            >
              {OPERATOR_OPTIONS.map((opt) => (
                <option
                  key={opt.value}
                  value={opt.value}
                  disabled={!eligibleOps.has(opt.value)}
                >
                  {opt.label}
                  {!eligibleOps.has(opt.value) ? '  — n/a' : ''}
                </option>
              ))}
            </select>
            {eligibleOps.size === 0 && (
              <p className="text-xs text-warn">
                No operators are eligible for this column type. Pick a
                different col_a or check the column's type union.
              </p>
            )}
          </label>

          {/* col_b for pairwise (non-not_null_when) */}
          {!isSum && !isNotNullWhen && (
            <label className="block space-y-1">
              <span className="text-xs uppercase tracking-wide text-muted">
                Compared column (col_b)
              </span>
              <select
                value={colB}
                onChange={(e) => setColB(e.target.value)}
                disabled={pending}
                className="block w-full rounded border border-border bg-paper px-2 py-1 text-sm font-mono"
                data-testid="add-rule-col-b"
              >
                <option value="">— pick a column —</option>
                {colBCandidates.map((c) => (
                  <option key={c.column} value={c.column}>
                    {c.column} ({c.inferred_type})
                  </option>
                ))}
              </select>
            </label>
          )}

          {/* not_null_when condition */}
          {isNotNullWhen && (
            <div
              className="space-y-2 rounded border border-border bg-surface-alt/20 px-3 py-2"
              data-testid="add-rule-condition-block"
            >
              <label className="block space-y-1">
                <span className="text-xs uppercase tracking-wide text-muted">
                  Condition column
                </span>
                <select
                  value={conditionCol}
                  onChange={(e) => setConditionCol(e.target.value)}
                  disabled={pending}
                  className="block w-full rounded border border-border bg-paper px-2 py-1 text-sm font-mono"
                  data-testid="add-rule-condition-col"
                >
                  {colBCandidates.map((c) => (
                    <option key={c.column} value={c.column}>
                      {c.column} ({c.inferred_type})
                    </option>
                  ))}
                </select>
              </label>
              <label className="block space-y-1">
                <span className="text-xs uppercase tracking-wide text-muted">
                  Condition value
                </span>
                <input
                  type="text"
                  value={conditionValue}
                  onChange={(e) => setConditionValue(e.target.value)}
                  disabled={pending}
                  placeholder='e.g. "complete"'
                  className="block w-full rounded border border-border bg-paper px-2 py-1 text-sm font-mono"
                  data-testid="add-rule-condition-value"
                />
              </label>
            </div>
          )}

          {/* sum_columns + tolerance */}
          {isSum && (
            <div
              className="space-y-2 rounded border border-border bg-surface-alt/20 px-3 py-2"
              data-testid="add-rule-sum-block"
            >
              <div className="space-y-1">
                <span className="text-xs uppercase tracking-wide text-muted">
                  Parts
                </span>
                <ul
                  className="flex flex-wrap gap-1"
                  data-testid="add-rule-sum-chips"
                >
                  {sumColumns.length === 0 && (
                    <li className="text-xs italic text-muted">
                      No parts — pick at least one numeric column.
                    </li>
                  )}
                  {sumColumns.map((col) => (
                    <li key={col} className="inline-flex">
                      <span
                        className="inline-flex items-center gap-1 rounded-full border border-border bg-paper px-2 py-0.5 text-xs font-mono"
                        data-testid={`add-rule-sum-part-${col}`}
                      >
                        <span>{col}</span>
                        <button
                          type="button"
                          onClick={() => removeSumPart(col)}
                          disabled={pending}
                          className="text-muted hover:text-danger focus:outline-none focus:ring-1 focus:ring-brand rounded-sm"
                          aria-label={`Remove ${col}`}
                          data-testid={`add-rule-sum-remove-${col}`}
                        >
                          ×
                        </button>
                      </span>
                    </li>
                  ))}
                </ul>
                <select
                  value=""
                  onChange={(e) => addSumPart(e.target.value)}
                  disabled={pending}
                  className="block w-full rounded border border-border bg-paper px-2 py-1 text-sm font-mono"
                  data-testid="add-rule-sum-add"
                >
                  <option value="">— add a part column —</option>
                  {sumPartCandidates
                    .filter((c) => !sumColumns.includes(c.column))
                    .map((c) => (
                      <option key={c.column} value={c.column}>
                        {c.column} ({c.inferred_type})
                      </option>
                    ))}
                </select>
              </div>
              <label className="block space-y-1">
                <span className="text-xs uppercase tracking-wide text-muted">
                  Tolerance
                </span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={tolerance}
                  onChange={(e) => {
                    const next = Number(e.target.value);
                    if (Number.isFinite(next) && next >= 0) {
                      setTolerance(next);
                    }
                  }}
                  disabled={pending}
                  className="w-32 rounded border border-border bg-paper px-2 py-1 text-xs font-mono"
                  data-testid="add-rule-tolerance"
                />
              </label>
            </div>
          )}

          {/* Severity */}
          <label className="block space-y-1">
            <span className="text-xs uppercase tracking-wide text-muted">
              Severity
            </span>
            <select
              value={severity}
              onChange={(e) => setSeverity(e.target.value as HitSeverity)}
              disabled={pending}
              className="block w-full rounded border border-border bg-paper px-2 py-1 text-sm"
              data-testid="add-rule-severity"
            >
              <option value="low">low</option>
              <option value="medium">medium</option>
              <option value="high">high</option>
            </select>
          </label>

          {/* Description (synthesised default, editable) */}
          <label className="block space-y-1">
            <span className="text-xs uppercase tracking-wide text-muted">
              Description
            </span>
            <textarea
              value={descriptionTouched ? description : synthesisedDescription}
              onChange={(e) => {
                setDescription(e.target.value);
                setDescriptionTouched(true);
              }}
              disabled={pending}
              rows={2}
              className="block w-full rounded border border-border bg-paper px-2 py-1 text-sm"
              data-testid="add-rule-description"
            />
            <p className="text-[11px] text-muted">
              Auto-filled from the rule shape; edit to override.
            </p>
          </label>

          {validationErrors.length > 0 && (
            <ul
              className="text-xs text-warn list-disc list-inside"
              data-testid="add-rule-validation"
            >
              {validationErrors.map((msg) => (
                <li key={msg}>{msg}</li>
              ))}
            </ul>
          )}
          {error && (
            <p className="text-sm text-danger" data-testid="add-rule-error">
              {error}
            </p>
          )}
        </div>

        <footer className="flex justify-end gap-2 border-t border-border px-6 py-4">
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={pending}
            data-testid="add-rule-close"
          >
            Close
          </Button>
          <Button
            variant="primary"
            onClick={onSubmit}
            disabled={submitDisabled}
            data-testid="add-rule-submit"
          >
            {pending ? 'Creating…' : 'Create rule'}
          </Button>
        </footer>
      </div>
    </div>
  );
}
